import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Container } from '../../layout/Container'
import { Card } from '../../ui/Card'
import { useScrollAnimation } from '../../../hooks/useScrollAnimation'

interface PhaseItem {
  id: string
  phase: string
  duration: string
  title: string
  outcome: string
  whatHappens: string
  whoParticipates: string
  whatYouKeep: string
}

export function HowWeWorkPhases() {
  const { t } = useTranslation()
  const { ref, isInView } = useScrollAnimation()

  const items = t('howWeWorkPage.phases.items', { returnObjects: true }) as PhaseItem[]

  return (
    <section ref={ref} className="py-24">
      <Container size="wide">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <span className="text-sm font-medium uppercase tracking-wider text-accent-light">
            {t('howWeWorkPage.phases.label')}
          </span>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-text-primary sm:text-4xl">
            {t('howWeWorkPage.phases.title')}
          </h2>
        </motion.div>

        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {items.map((phase, index) => (
            <motion.div
              key={phase.id}
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card variant="elevated" className="h-full">
                <div className="flex items-start justify-between gap-4">
                  <span className="text-4xl font-semibold text-gradient tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-accent-light">
                    {phase.duration}
                  </span>
                </div>

                <p className="mt-4 text-sm font-medium uppercase tracking-wider text-text-muted">
                  {phase.phase}
                </p>
                <h3 className="mt-2 text-xl font-medium text-text-primary">{phase.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-accent-light">{phase.outcome}</p>

                <div className="mt-6 space-y-4 border-t border-border pt-6">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                      {t('howWeWorkPage.phases.whatHappens')}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-text-secondary">{phase.whatHappens}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                      {t('howWeWorkPage.phases.whoParticipates')}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-text-secondary">{phase.whoParticipates}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium uppercase tracking-wider text-text-muted">
                      {t('howWeWorkPage.phases.whatYouKeep')}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-text-secondary">{phase.whatYouKeep}</p>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  )
}
